import { useState } from 'preact/hooks';
import { Download } from 'lucide-preact';
import { TextBoxField, Select, DateTimePicker, FileUpload, TimeZoneCombobox } from '@/components';
import { Controller, type Control, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import type { SelectOption } from '@/components/common/Select';
import type { TouchPointFormData } from '../TouchPointModal';

interface MeetingPropertiesSectionProps {
  control: Control<TouchPointFormData>;
  register: UseFormRegister<TouchPointFormData>;
  errors: FieldErrors<TouchPointFormData>;
  meetingTypeOptions: SelectOption[];
  currentStartTime: string | undefined;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  existingAttachmentUrl: string;
}

export function MeetingPropertiesSection({
  control,
  register,
  errors,
  meetingTypeOptions,
  currentStartTime,
  selectedFile,
  setSelectedFile,
  existingAttachmentUrl,
}: MeetingPropertiesSectionProps) {
  const [fileError, setFileError] = useState('');

  return (
    <div className="space-y-6 pt-4 border-t border-slate-200 dark:border-slate-600 md:border-0 md:pt-0">
      <Select
        label="Meeting Type"
        required
        options={meetingTypeOptions}
        placeholder="Select meeting type..."
        error={errors.MeetingType?.message}
        {...register('MeetingType', {
          required: 'Meeting type is required'
        })}
      />

      <Controller
        name="TimeZone"
        control={control}
        rules={{ required: 'Time zone is required' }}
        render={({ field, fieldState }) => (
          <TimeZoneCombobox
            label="Time Zone"
            required
            name="TimeZone"
            value={field.value}
            onChange={field.onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <DateTimePicker
        label="Start Time"
        required
        error={errors.StartTime?.message}
        {...register('StartTime', {
          required: 'Start time is required'
        })}
      />

      <DateTimePicker
        label="End Time"
        required
        error={errors.EndTime?.message}
        helperText="Must be after start time"
        {...register('EndTime', {
          required: 'End time is required',
          validate: (value) => {
            const start = currentStartTime;
            if (start && value && new Date(value) <= new Date(start)) {
              return 'End time must be after start time';
            }
            return true;
          }
        })}
      />

      <TextBoxField
        control={control}
        name="MeetingUrlLink"
        label="Meeting Link"
        type="url"
        placeholder="https://meet.google.com/..."
      />

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Attachment
        </label>

        {/* Show existing attachment download link if available */}
        {existingAttachmentUrl && !selectedFile && (
          <div className="mb-2">
            <a
              href={existingAttachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <Download size={16} />
              Current attachment
            </a>
          </div>
        )}

        <FileUpload
          selectedFile={selectedFile}
          onFileChange={(file) => {
            // A refused file arrives as null right after onError, so only a real file clears it.
            if (file) setFileError('');
            setSelectedFile(file);
          }}
          onError={setFileError}
          error={fileError}
          accept=".txt,.json"
          maxSizeMB={10}
          helperText="TXT, or a Fireflies or Zoom JSON export (max 10MB)"
        />
      </div>
    </div>
  );
}
